/**
 * Pure report-preset projection.
 *
 * A preset is presentation state, so this module deliberately consumes the
 * existing project/index/cost derivations and never adds report-specific
 * business rules to the core domain.  It also never mutates `Project` (or its
 * nested collections): consumers receive fresh, selected field values.
 */

import { buildProjectIndex, rawNumber, type Project } from '@bigint-project/core';

import type { ReportFieldId, ReportPreset } from '../store/types';
import { selectCostResult, selectCurrentCostResult, selectOverallocatedResourceIds } from './derivations';

export type ReportValue = string | number | boolean | null;

export interface ReportProjectedRow {
  readonly id: string;
  /** Present only for task rows; retained even when it is not a selected field. */
  readonly outlineLevel: number | null;
  /** Exactly the fields selected by the sanitized preset, in preset order. */
  readonly values: Readonly<Partial<Record<ReportFieldId, ReportValue>>>;
}

export interface ReportProjectionGroup {
  readonly key: string;
  readonly label: string | null;
  readonly rows: readonly ReportProjectedRow[];
}

export interface ReportChartSeries {
  readonly field: ReportFieldId;
  /** `null` is an unavailable authoritative value, never a synthetic zero. */
  readonly points: readonly { readonly rowId: string; readonly label: string; readonly value: number | null }[];
}

export interface ReportChartProjection {
  readonly categoryField: ReportFieldId | null;
  readonly series: readonly ReportChartSeries[];
}

export interface ReportComparisonProjection {
  readonly categoryField: ReportFieldId | null;
  readonly left: ReportChartSeries;
  readonly right: ReportChartSeries;
}

export interface ReportProjection {
  readonly presetId: string;
  readonly dataset: ReportPreset['dataset'];
  readonly layout: ReportPreset['layout'];
  readonly fields: readonly ReportFieldId[];
  /** Groups contain the same ordered rows as `rows`, without sharing mutable arrays. */
  readonly groups: readonly ReportProjectionGroup[];
  readonly rows: readonly ReportProjectedRow[];
  /** Table consumers use this directly; non-table layouts leave it null. */
  readonly table: { readonly columns: readonly ReportFieldId[]; readonly groups: readonly ReportProjectionGroup[] } | null;
  readonly chart: ReportChartProjection | null;
  readonly comparison: ReportComparisonProjection | null;
}

type AllValues = Readonly<Record<ReportFieldId, ReportValue>>;

interface CandidateRow {
  readonly id: string;
  readonly outlineLevel: number | null;
  readonly originalIndex: number;
  readonly values: AllValues;
}

const NUMERIC_FIELDS = new Set<ReportFieldId>([
  'duration', 'work', 'percentComplete', 'cost', 'actualCost', 'remainingCost', 'variance', 'maxUnits',
]);

function selectedRow(candidate: CandidateRow, fields: readonly ReportFieldId[]): ReportProjectedRow {
  const values: Partial<Record<ReportFieldId, ReportValue>> = {};
  for (const field of fields) values[field] = candidate.values[field];
  return { id: candidate.id, outlineLevel: candidate.outlineLevel, values };
}

function compareValues(left: ReportValue, right: ReportValue): number {
  // Missing baseline variance and other unavailable values always sort last;
  // reversing that for descending order makes a report unexpectedly lead with
  // unavailable data.
  if (left === null) return right === null ? 0 : 1;
  if (right === null) return -1;
  if (typeof left === 'boolean' && typeof right === 'boolean') return Number(left) - Number(right);
  return left < (right as string | number) ? -1 : left > (right as string | number) ? 1 : 0;
}

function compareWbs(left: ReportValue, right: ReportValue): number {
  if (typeof left !== 'string' || typeof right !== 'string') return compareValues(left, right);
  const leftParts = left.split('.').map(Number);
  const rightParts = right.split('.').map(Number);
  const length = Math.max(leftParts.length, rightParts.length);
  for (let index = 0; index < length; index += 1) {
    const difference = (leftParts[index] ?? -1) - (rightParts[index] ?? -1);
    if (difference !== 0) return difference;
  }
  return 0;
}

function stableSort(rows: readonly CandidateRow[], preset: ReportPreset): readonly CandidateRow[] {
  return [...rows].sort((left, right) => {
    for (const sort of preset.sorts) {
      const leftValue = left.values[sort.field];
      const rightValue = right.values[sort.field];
      if (leftValue === null || rightValue === null) {
        if (leftValue !== rightValue) return leftValue === null ? 1 : -1;
        continue;
      }
      const compared = sort.field === 'wbs'
        ? compareWbs(leftValue, rightValue)
        : compareValues(leftValue, rightValue);
      if (compared !== 0) return sort.direction === 'asc' ? compared : -compared;
    }
    return left.originalIndex - right.originalIndex;
  });
}

function taskMatches(candidate: CandidateRow, preset: ReportPreset, project: Project): boolean {
  const task = project.tasks.find((entry) => (entry.id as string) === candidate.id);
  if (task === undefined) return false;
  if (preset.outlineLevel !== null && task.outlineLevel > preset.outlineLevel) return false;
  switch (preset.filter) {
    case 'all': return true;
    case 'active': return task.percentComplete < 100;
    case 'completed': return task.percentComplete >= 100;
    case 'late':
      return task.percentComplete < 100 && (
        (project.statusDate !== null && (task.finish as string) < (project.statusDate as string)) ||
        (task.deadline !== null && (task.finish as string) > (task.deadline as string))
      );
    case 'critical': return task.isCritical;
    case 'milestone': return task.isMilestone;
    default: return false;
  }
}

function resourceMatches(candidate: CandidateRow, preset: ReportPreset): boolean {
  switch (preset.filter) {
    case 'all': return true;
    case 'overallocated': return candidate.values.isOverallocated === true;
    case 'budget': return candidate.values.isBudget === true;
    default: return false;
  }
}

function buildTaskCandidates(project: Project): readonly CandidateRow[] {
  const index = buildProjectIndex(project);
  const currentCostByTask = new Map(
    (selectCurrentCostResult(project)?.byTask ?? []).map((entry) => [entry.taskId as string, entry]),
  );
  const baselineCostByTask = new Map(
    (project.baselines.some((baseline) => baseline.number === 0) ? selectCostResult(project)?.byTask ?? [] : [])
      .map((entry) => [entry.taskId as string, entry]),
  );

  return index.outlineOrder.map((taskId, originalIndex) => {
    const task = index.tasksById.get(taskId)!;
    const current = currentCostByTask.get(taskId);
    const baseline = baselineCostByTask.get(taskId);
    return {
      id: taskId,
      outlineLevel: task.outlineLevel,
      originalIndex,
      values: {
        name: task.name,
        wbs: task.wbs,
        start: task.start as string,
        finish: task.finish as string,
        duration: rawNumber(task.durationMinutes),
        work: rawNumber(task.work),
        percentComplete: task.percentComplete,
        cost: current === undefined ? null : rawNumber(current.cost),
        actualCost: current === undefined ? null : rawNumber(current.actualCost),
        remainingCost: current === undefined ? null : rawNumber(current.remainingCost),
        variance: baseline?.variance === null || baseline === undefined ? null : rawNumber(baseline.variance),
        isCritical: task.isCritical,
        isMilestone: task.isMilestone,
        type: null,
        group: null,
        maxUnits: null,
        isOverallocated: null,
        isBudget: null,
      },
    };
  });
}

function buildResourceCandidates(project: Project): readonly CandidateRow[] {
  const index = buildProjectIndex(project);
  const currentCostByResource = new Map(
    (selectCurrentCostResult(project)?.byResource ?? []).map((entry) => [entry.resourceId as string, entry]),
  );
  const overallocated = selectOverallocatedResourceIds(project);

  return project.resources.map((resource, originalIndex) => {
    const current = currentCostByResource.get(resource.id as string);
    const work = (index.assignmentsByResourceId.get(resource.id as string) ?? [])
      .reduce((total, assignment) => total + rawNumber(assignment.work), 0);
    return {
      id: resource.id as string,
      outlineLevel: null,
      originalIndex,
      values: {
        name: resource.name,
        wbs: null,
        start: null,
        finish: null,
        duration: null,
        work,
        percentComplete: null,
        cost: current === undefined ? null : rawNumber(current.cost),
        actualCost: current === undefined ? null : rawNumber(current.actualCost),
        remainingCost: current === undefined ? null : rawNumber(current.remainingCost),
        variance: null,
        isCritical: null,
        isMilestone: null,
        type: resource.type,
        group: resource.group,
        maxUnits: rawNumber(resource.maxUnits),
        isOverallocated: overallocated.has(resource.id as string),
        isBudget: resource.isBudget === true,
      },
    };
  });
}

function groupAndSort(rows: readonly CandidateRow[], preset: ReportPreset): readonly ReportProjectionGroup[] {
  if (preset.groupBy === 'none') {
    return [{ key: 'all', label: null, rows: stableSort(rows, preset).map((row) => selectedRow(row, preset.fields)) }];
  }

  const byLevel = new Map<number, CandidateRow[]>();
  for (const row of rows) {
    const level = row.outlineLevel ?? 0;
    const group = byLevel.get(level) ?? [];
    group.push(row);
    byLevel.set(level, group);
  }
  return [...byLevel.entries()]
    .sort(([left], [right]) => left - right)
    .map(([level, group]) => ({
      key: `outline-${level}`,
      label: `Outline level ${level}`,
      rows: stableSort(group, preset).map((row) => selectedRow(row, preset.fields)),
    }));
}

function makeSeries(rows: readonly ReportProjectedRow[], field: ReportFieldId, categoryField: ReportFieldId | null): ReportChartSeries {
  return {
    field,
    points: rows.map((row) => ({
      rowId: row.id,
      label: categoryField === null ? row.id : String(row.values[categoryField] ?? row.id),
      value: typeof row.values[field] === 'number' ? row.values[field] : null,
    })),
  };
}

/** Build a display-only Table, Chart, or Comparison dataset from a sanitized preset. */
export function buildReportProjection(project: Project, preset: ReportPreset): ReportProjection {
  const candidates = preset.dataset === 'Tasks' ? buildTaskCandidates(project) : buildResourceCandidates(project);
  const filtered = candidates.filter((candidate) =>
    preset.dataset === 'Tasks' ? taskMatches(candidate, preset, project) : resourceMatches(candidate, preset),
  );
  const groups = groupAndSort(filtered, preset);
  const rows = groups.flatMap((group) => group.rows);
  const categoryField = preset.fields.find((field) => !NUMERIC_FIELDS.has(field)) ?? null;
  const numericFields = preset.fields.filter((field) => NUMERIC_FIELDS.has(field));
  const chart = preset.layout === 'Chart'
    ? { categoryField, series: numericFields.map((field) => makeSeries(rows, field, categoryField)) }
    : null;
  const comparison = preset.layout === 'Comparison' && numericFields.length >= 2
    ? {
      categoryField,
      left: makeSeries(rows, numericFields[0]!, categoryField),
      right: makeSeries(rows, numericFields[1]!, categoryField),
    }
    : null;

  return {
    presetId: preset.id,
    dataset: preset.dataset,
    layout: preset.layout,
    fields: [...preset.fields],
    groups,
    rows,
    table: preset.layout === 'Table' ? { columns: [...preset.fields], groups } : null,
    chart,
    comparison,
  };
}
